/**
 * Safety Manager extractors — project rows from FLD / SafetyPoint /
 * LogicSolver queries into typed Safety-tab shapes. Pure functions,
 * no RTK / no Neo4j driver.
 */

import { extractNode, extractNodeList, toIntCount } from '../graph-row'
import { compareByNameThenId } from '../sort'
import type {
  CypherReadResult,
  PlantEquipment,
  PlantEquipmentSafetyPointLink,
  PlantFunctionLogicDiagram,
  PlantFunctionLogicDiagramDetail,
  PlantFunctionLogicDiagramSummary,
  PlantLogicSolver,
  PlantLogicSolverDetail,
  PlantProtectiveFunction,
  PlantProtectiveFunctionIndex,
  PlantProtectiveFunctionSummary,
  PlantSafetyPoint,
  PlantSafetyPointDetail,
  PlantSite
} from '../types'
import {
  groupProtectiveFunctionSummaries,
  matchesProtectiveFunctionSearch
} from './protective-functions'

function optionalString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim().length > 0 ? value : undefined
}

/** Project a landing-query row into a {@link PlantFunctionLogicDiagramSummary}. */
export function mapPlantFunctionLogicDiagramSummaryRow(
  row: Record<string, unknown>
): PlantFunctionLogicDiagramSummary | null {
  const diagram = extractNode<PlantFunctionLogicDiagram>(row['diagram'])
  if (!diagram) return null
  const solver = extractNode<PlantLogicSolver>(row['solver']) ?? undefined
  const site = extractNode<PlantSite>(row['site']) ?? undefined
  return {
    ...diagram,
    ...(solver ? { solver } : {}),
    ...(site ? { site } : {}),
    pointCount: toIntCount(row['pointCount']),
    implementedBy: extractNodeList<PlantProtectiveFunction>(row['implementedBy']).sort(
      compareByNameThenId
    )
  }
}

function mapFldRows(value: unknown, solver?: PlantLogicSolver): PlantFunctionLogicDiagramSummary[] {
  if (!Array.isArray(value)) return []
  const out: PlantFunctionLogicDiagramSummary[] = []
  const seen = new Set<string>()
  for (const item of value) {
    if (!item || typeof item !== 'object') continue
    const row = item as Record<string, unknown>
    const mapped = mapPlantFunctionLogicDiagramSummaryRow({
      diagram: row['diagram'],
      solver,
      pointCount: row['pointCount'],
      implementedBy: row['implementedBy']
    })
    if (!mapped || seen.has(mapped.id)) continue
    seen.add(mapped.id)
    out.push(mapped)
  }
  out.sort(compareByNameThenId)
  return out
}

/** Project the LogicSolver detail-query result. */
export function mapPlantLogicSolverDetail(
  raw: CypherReadResult,
  solverId: string
): PlantLogicSolverDetail {
  const row = raw.results[0] ?? {}
  const solver = extractNode<PlantLogicSolver>(row['solver']) ?? {
    id: solverId,
    _id: solverId,
    name: solverId
  }
  const site = extractNode<PlantSite>(row['site']) ?? undefined
  return {
    solver,
    ...(site ? { site } : {}),
    diagrams: mapFldRows(row['fldRows'], solver),
    functions: extractNodeList<PlantProtectiveFunction>(row['functions']).sort(compareByNameThenId)
  }
}

/** Project the FunctionLogicDiagram detail-query result. */
export function mapPlantFunctionLogicDiagramDetail(
  raw: CypherReadResult,
  fldId: string
): PlantFunctionLogicDiagramDetail {
  const row = raw.results[0] ?? {}
  const diagram = extractNode<PlantFunctionLogicDiagram>(row['diagram']) ?? {
    id: fldId,
    _id: fldId,
    name: fldId
  }
  const solver = extractNode<PlantLogicSolver>(row['solver']) ?? undefined
  const site = extractNode<PlantSite>(row['site']) ?? undefined
  return {
    diagram,
    ...(solver ? { solver } : {}),
    ...(site ? { site } : {}),
    points: extractNodeList<PlantSafetyPoint>(row['points']).sort(compareByNameThenId),
    functions: extractNodeList<PlantProtectiveFunction>(row['functions']).sort(compareByNameThenId)
  }
}

/** Project the SafetyPoint detail-query result. */
export function mapPlantSafetyPointDetail(
  raw: CypherReadResult,
  pointId: string
): PlantSafetyPointDetail {
  const row = raw.results[0] ?? {}
  const point = extractNode<PlantSafetyPoint>(row['point']) ?? {
    id: pointId,
    _id: pointId,
    name: pointId
  }
  const diagram = extractNode<PlantFunctionLogicDiagram>(row['diagram']) ?? undefined
  const solver = extractNode<PlantLogicSolver>(row['solver']) ?? undefined
  const site = extractNode<PlantSite>(row['site']) ?? undefined
  return {
    point,
    ...(diagram ? { diagram } : {}),
    ...(solver ? { solver } : {}),
    ...(site ? { site } : {}),
    functions: extractNodeList<PlantProtectiveFunction>(row['functions']).sort(compareByNameThenId),
    equipment: extractNodeList<PlantEquipment>(row['equipment']).sort(compareByNameThenId)
  }
}

/** Project equipment → SafetyPoint reverse-lookup rows. */
export function mapPlantEquipmentSafetyPoints(
  raw: CypherReadResult
): PlantEquipmentSafetyPointLink[] {
  const out: PlantEquipmentSafetyPointLink[] = []
  const seen = new Set<string>()
  for (const row of raw.results) {
    const point = extractNode<PlantSafetyPoint>(row['point'])
    if (!point || seen.has(point.id)) continue
    seen.add(point.id)
    const diagram = extractNode<PlantFunctionLogicDiagram>(row['diagram']) ?? undefined
    const solver = extractNode<PlantLogicSolver>(row['solver']) ?? undefined
    out.push({
      point,
      ...(diagram ? { diagram } : {}),
      ...(solver ? { solver } : {}),
      functions: extractNodeList<PlantProtectiveFunction>(row['functions']).sort(
        compareByNameThenId
      )
    })
  }
  out.sort((a, b) => compareByNameThenId(a.point, b.point))
  return out
}

/** True when an FLD landing row matches a free-text Safety-tab search. */
export function matchesSafetyDiagramSearch(
  diagram: PlantFunctionLogicDiagramSummary,
  query: string
): boolean {
  const needle = query.trim().toLowerCase()
  if (!needle) return true
  const haystack = [
    diagram.name,
    diagram.id,
    diagram.fld_number,
    diagram.description,
    diagram.application,
    diagram.solver?.name,
    diagram.solver?.type,
    diagram.solver?.vendor,
    diagram.solver?.application,
    diagram.site?.name,
    ...(diagram.implementedBy ?? []).flatMap((fn) => [fn.name, fn.id])
  ]
    .filter((value): value is string => typeof value === 'string' && value.length > 0)
    .join('\n')
    .toLowerCase()
  return haystack.includes(needle)
}

function matchesSolverSearch(solver: PlantLogicSolver, query: string): boolean {
  const needle = query.trim().toLowerCase()
  if (!needle) return true
  const haystack = [solver.name, solver.id, solver.type, solver.vendor, solver.application]
    .filter((value): value is string => typeof value === 'string' && value.length > 0)
    .join('\n')
    .toLowerCase()
  return haystack.includes(needle)
}

/**
 * Fold Safety Manager FLD rows onto the SRS protective-function groups.
 * Solvers that only have FLDs (no `SOLVED_BY` functions) still appear.
 */
export function mergeSafetyLanding(
  summaries: PlantProtectiveFunctionSummary[],
  diagrams: PlantFunctionLogicDiagramSummary[]
): PlantProtectiveFunctionIndex {
  const index = groupProtectiveFunctionSummaries(summaries)
  const diagramsBySolver = new Map<string, PlantFunctionLogicDiagramSummary[]>()
  const unsolvedDiagrams: PlantFunctionLogicDiagramSummary[] = []

  for (const diagram of diagrams) {
    if (!diagram.solver) {
      unsolvedDiagrams.push(diagram)
      continue
    }
    const list = diagramsBySolver.get(diagram.solver.id) ?? []
    list.push(diagram)
    diagramsBySolver.set(diagram.solver.id, list)
  }

  for (const group of index.solvers) {
    const flds = diagramsBySolver.get(group.solver.id) ?? []
    flds.sort(compareByNameThenId)
    group.diagrams = flds
    if (optionalString(group.solver.application) === undefined) {
      const application = flds.find((fld) => optionalString(fld.solver?.application))?.solver
        ?.application
      if (application) group.solver = { ...group.solver, application }
    }
    diagramsBySolver.delete(group.solver.id)
  }

  for (const flds of diagramsBySolver.values()) {
    const solver = flds[0]?.solver
    if (!solver) continue
    flds.sort(compareByNameThenId)
    index.solvers.push({ solver, functions: [], diagrams: flds })
  }

  index.solvers.sort((a, b) => compareByNameThenId(a.solver, b.solver))
  unsolvedDiagrams.sort(compareByNameThenId)
  index.unsolvedDiagrams = unsolvedDiagrams
  return index
}

/**
 * Filter a merged Safety landing by free-text search. A solver-name hit
 * keeps the whole group; otherwise only matching functions / FLDs remain.
 */
export function filterSafetyLanding(
  index: PlantProtectiveFunctionIndex,
  query: string
): PlantProtectiveFunctionIndex {
  const needle = query.trim()
  if (!needle) return index

  const solvers = []
  for (const group of index.solvers) {
    if (matchesSolverSearch(group.solver, needle)) {
      solvers.push(group)
      continue
    }
    const functions = group.functions.filter((fn) => matchesProtectiveFunctionSearch(fn, needle))
    const diagrams = group.diagrams.filter((diagram) => matchesSafetyDiagramSearch(diagram, needle))
    if (functions.length === 0 && diagrams.length === 0) continue
    solvers.push({ solver: group.solver, functions, diagrams })
  }

  return {
    solvers,
    unsolved: index.unsolved.filter((fn) => matchesProtectiveFunctionSearch(fn, needle)),
    unsolvedDiagrams: index.unsolvedDiagrams.filter((diagram) =>
      matchesSafetyDiagramSearch(diagram, needle)
    )
  }
}

/**
 * Filter a solver's FLD catalog: optional "linked to a SIF" gate plus
 * the same free-text haystack as the Safety landing search.
 */
export function filterSolverDiagrams(
  diagrams: PlantFunctionLogicDiagramSummary[],
  query: string,
  linkedOnly: boolean
): PlantFunctionLogicDiagramSummary[] {
  return diagrams.filter((diagram) => {
    if (linkedOnly && (diagram.implementedBy ?? []).length === 0) return false
    return matchesSafetyDiagramSearch(diagram, query)
  })
}
